import { useMutation } from '@tanstack/react-query';

import {
  useInvalidateChannelById,
  useInvalidateChannelsList,
} from '@/entities/channel';
import { ApiError } from '@/shared/api/ApiError';
import {
  connectWildberries,
  type WildberriesConnectResponse,
} from '@/shared/api/channels/wildberries';
import { useNotifications } from '@/shared/ui/Notifications';

interface WildberriesConnectInput {
  channelId: string;
  token: string;
  name?: string;
}

export function useWildberriesConnect() {
  const invalidateChannelsList = useInvalidateChannelsList();
  const invalidateChannelById = useInvalidateChannelById();
  const { addNotification } = useNotifications();

  const mutation = useMutation<WildberriesConnectResponse, ApiError, WildberriesConnectInput>({
    mutationFn: ({ channelId, token, name }) =>
      connectWildberries(channelId, {
        token,
        name: name?.trim() || undefined,
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidateChannelsList(),
        invalidateChannelById(variables.channelId),
      ]);
    },
    onError: (error) => {
      addNotification({
        title: error.message,
        caption: error?.data?.details,
      });
    },
  });

  return {
    connect: (input: WildberriesConnectInput) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
    reset: mutation.reset,
  };
}
